/* eslint-disable object-shorthand */
import { Box, MenuItem, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';
import Button from '~/components/common/Button';
import themes from '~/themes';
import { Form, Formik } from 'formik';
import { useUpdateConnectionMutation } from '~/services/api/userManagement';
import { IStore, StorePlatform } from '~/models/stores';
import { useLocation } from 'react-router-dom';
import { fromQueryString } from '~/utils/queryString';
import { isNullOrUndefined } from '~/utils/common';
import { alertParams, showAlert } from '~/components/common/Alert';
import { Select } from '~/components/common/Select';
import { AddZoneTypeSchema } from '~/utils/schema/integration';
import { CheckOutlined } from '@mui/icons-material';
import { useIsFetching } from '@tanstack/react-query';
import { queryKeys } from '~/services/api/queryKeys';
import ActionBar from '~/pages/AdminOptions/components/ActionBar';
import { useDebounce } from 'use-debounce';
import { DrawnZones } from './components/DrawnZone/DrawnZones';
import { DeliveryZoneSets, IDefaultCenter } from './types';
import { PostalCode } from './components/PostalCode/PostalCode';
import { DistanceRadius } from './components/DistanceRadius/DistanceRadius';

type ZoneAndFreeProps = { data: IStore };

export const ZoneAndFree: React.FC<ZoneAndFreeProps> = ({ data }) => {
  const location = useLocation();
  const search = fromQueryString(location.search);
  const platform = search.platform as StorePlatform;
  const fetchingStore = useIsFetching([queryKeys.stores.getListIntegration]);
  const [debouncedFetchingStore] = useDebounce(fetchingStore, 150);

  const { mutate: updateConnectionMutation, isLoading: isUpdateLoading } =
    useUpdateConnectionMutation();

  const defaultCenter: IDefaultCenter =
    !isNullOrUndefined(data.hubs?.[0]?.lng) &&
    !isNullOrUndefined(data.hubs?.[0]?.lng)
      ? {
          lat: data.hubs[0].lat,
          lng: data.hubs[0].lng,
        }
      : undefined;

  const renderDeliverySetting = useCallback(
    (type: DeliveryZoneSets, defaultCenterItem?: IDefaultCenter) => {
      switch (type) {
        case DeliveryZoneSets.DrawnZones:
          return <DrawnZones defaultCenter={defaultCenterItem} />;
        case DeliveryZoneSets.PostalCode:
          return <PostalCode />;
        case DeliveryZoneSets.DistanceRadius:
          return <DistanceRadius />;
        default:
          return <div />;
      }
    },
    [],
  );

  const onSubmit = (values) => {
    const { area_type } = values;
    showAlert({
      ...alertParams.warning,
      description:
        'Your changes may affect your order limit settings.\nPlease review and adjust them.',
      okText: 'Continue',
      cancelText: 'Cancel',
      onOk: () =>
        updateConnectionMutation({
          id: data.id,
          area_type: area_type,
        }),
    });
  };

  if (platform === StorePlatform.Breadstack) {
    return (
      <Stack spacing={2}>
        <Typography
          sx={{
            borderRadius: '5px',
            background: themes.bg.lightPurple,
            padding: 1,
          }}
        >
          You can create delivery zones and add a different delivery fee for
          each zone. Delivery Zone can be defined by drawn zone or postal codes
          or distance radius.
        </Typography>

        <Button
          noRounder
          buttonType='default'
          sx={{ width: 'fit-content' }}
          onClick={() => window.open(data.breadstack_url, '_blank')}
        >
          Manage in Breadstack
        </Button>
      </Stack>
    );
  }

  return (
    <Stack>
      <Formik
        initialValues={{
          area_type: data.area_type || DeliveryZoneSets.DrawnZones,
        }}
        onSubmit={onSubmit}
        enableReinitialize
        validationSchema={AddZoneTypeSchema}
      >
        {({ dirty, values, setFieldValue, resetForm, submitForm }) => (
          <Form
            id='update-distance'
            onKeyPress={(e) => {
              if (e.code === 'Enter') {
                e.preventDefault();
              }
            }}
            style={{
              paddingBottom: !fetchingStore && dirty ? '55px' : 0,
            }}
          >
            <Stack mt={0.4} mb={1} gap={1.6}>
              <Typography
                variant='h5'
                color={themes.color.violet500}
                fontWeight='bold'
              >
                Set Zones & Fees
              </Typography>
              <Select
                legend='How would you like to define the zones?'
                name='area_type'
                id='area_type'
                options={[
                  {
                    value: DeliveryZoneSets.DrawnZones,
                    label: 'Drawn Zone',
                  },
                  { value: DeliveryZoneSets.PostalCode, label: 'Postal Code' },
                  {
                    value: DeliveryZoneSets.DistanceRadius,
                    label: 'Distance Radius',
                  },
                ]}
                fullWidth
                value={values?.area_type}
                onChange={(e: any) => {
                  showAlert({
                    ...alertParams.warnDark,
                    title: 'Are You Sure To Change The Delivery Area Type?',
                    description:
                      'Are you sure to change the delivery area type?',
                    onOk: () => {
                      resetForm();
                      setFieldValue('area_type', e.target.value);
                    },
                    cancelText: 'Cancel',
                    sx: { '& .MuiPaper-root': { maxWidth: 498 } },
                    sxDescription: { color: '#160E4D' },
                    sxButton: {
                      borderRadius: '20px !important',
                      width: '125px',
                    },
                  });
                }}
                paperMaxHeight={400}
                renderOption={(option) => (
                  <MenuItem
                    sx={{
                      'display': 'flex',
                      'alignItems': 'center',
                      'gap': '8px',
                      '&.Mui-selected': {
                        backgroundColor: '#EDF0F7 !important',
                      },
                    }}
                    key={option?.value}
                    value={option?.value || ''}
                  >
                    <CheckOutlined
                      sx={{
                        fontSize: 16,
                        color:
                          option.value === values.area_type
                            ? themes.color.violet500
                            : 'transparent',
                      }}
                    />

                    {option.label}
                  </MenuItem>
                )}
              />
            </Stack>
            {renderDeliverySetting(values.area_type, defaultCenter)}
            {!debouncedFetchingStore && dirty ? (
              <Box className='save-change-btn'>
                <ActionBar
                  onCancel={resetForm}
                  onSave={submitForm}
                  loading={isUpdateLoading}
                />
              </Box>
            ) : null}
          </Form>
        )}
      </Formik>
    </Stack>
  );
};
